import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Box,
  Collapse,
  Skeleton,
  Stack,
  Typography,
  alpha,
  keyframes,
} from "@mui/material";

import type {
  DagNode as DagNodeType,
  DagEdge as DagEdgeType,
} from "src/domains/application/hooks/usePipelineDag";

import {
  PHASE_LABELS,
  STATUS_COLORS,
  type DagPhase,
  type DagStatus,
} from "./dag-types";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PipelineDAGProps {
  nodes: DagNodeType[];
  edges: DagEdgeType[];
  phases: DagPhase[];
  selectedNodeId: string | null;
  onNodeClick: (nodeId: string, data: DagNodeType["data"]) => void;
  onExecutionClick?: (nodeId: string, executionId: number) => void;
  /** When true, shows a skeleton DAG instead of the empty state. */
  loading?: boolean;
}

// ── Edge geometry ─────────────────────────────────────────────────────────────

interface EdgePath {
  d: string;
  status: DagStatus;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ── Status dot ────────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: DagStatus }): React.ReactElement {
  const color = STATUS_COLORS[status];

  if (status === "running") {
    return (
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          flexShrink: 0,
          background: `conic-gradient(${color} 270deg, ${alpha(color, 0.2)} 270deg 360deg)`,
          animation: `${spin} 1s linear infinite`,
        }}
      />
    );
  }

  if (status === "skipped") {
    return (
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          border: `2px dashed ${color}`,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ fontSize: 9, color, lineHeight: 1 }}>–</Typography>
      </Box>
    );
  }

  if (status === "pending") {
    return (
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          border: `2px solid ${color}`,
          flexShrink: 0,
        }}
      />
    );
  }

  // success / failed
  const glyph = status === "success" ? "✓" : "✕";
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        bgcolor: color,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        sx={{ fontSize: 9, color: "#fff", fontWeight: 700, lineHeight: 1 }}
      >
        {glyph}
      </Typography>
    </Box>
  );
}

// ── Job node ──────────────────────────────────────────────────────────────────

function JobNode({
  node,
  selected,
  onClick,
  onExecutionClick,
}: {
  node: DagNodeType;
  selected: boolean;
  onClick: (id: string, data: DagNodeType["data"]) => void;
  onExecutionClick?: (nodeId: string, executionId: number) => void;
}): React.ReactElement {
  const { status, type, duration, label, message, executions } = node.data;
  const [executionsOpen, setExecutionsOpen] = useState(false);
  const color = STATUS_COLORS[status];
  const opacity = status === "skipped" ? 0.5 : status === "pending" ? 0.75 : 1;

  return (
    <Box
      data-node-id={node.id}
      onClick={() => onClick(node.id, node.data)}
      role="button"
      tabIndex={0}
      sx={{
        borderRadius: "10px",
        bgcolor: (t) => t.palette.background.paper,
        border: (t) =>
          status === "skipped"
            ? `1px dashed ${t.palette.divider}`
            : `1px solid ${t.palette.divider}`,
        borderLeft: `3px solid ${color}`,
        opacity,
        cursor: "pointer",
        px: 1.5,
        py: 1.125,
        mb: 1.75,
        boxShadow: selected
          ? (t) =>
              status === "failed"
                ? `0 0 0 2.5px ${alpha("#f43f5e", 0.25)}`
                : `0 0 0 2.5px ${alpha(t.palette.primary.main, 0.25)}`
          : "0 1px 2px rgba(0,0,0,0.04)",
        transition:
          "box-shadow 0.15s ease, border-color 0.15s ease, opacity 0.15s ease",
        "&:hover": {
          borderColor: (t) => alpha(t.palette.text.primary, 0.2),
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <StatusDot status={status} />
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            flex: 1,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
        {type && (
          <Box
            sx={{
              fontSize: "9.5px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "text.secondary",
              px: 0.625,
              py: 0.125,
              borderRadius: 0.5,
              bgcolor: (t) => alpha(t.palette.text.primary, 0.08),
              flexShrink: 0,
            }}
          >
            {type}
          </Box>
        )}
      </Stack>
      {duration && (
        <Typography
          sx={{
            fontSize: 10,
            color: "text.secondary",
            mt: 0.375,
            pl: 3,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {duration}
        </Typography>
      )}
      {status === "failed" && message && (
        <Typography
          title={message}
          sx={{
            fontSize: 10,
            color: "error.main",
            mt: 0.25,
            pl: 3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
          }}
        >
          {message}
        </Typography>
      )}
      {executions.length > 0 && (
        <Box sx={{ mt: 0.75, pl: 3 }}>
          <Typography
            component="button"
            onClick={(event) => {
              event.stopPropagation();
              setExecutionsOpen((open) => !open);
            }}
            sx={{
              border: 0,
              background: "transparent",
              color: "text.secondary",
              cursor: "pointer",
              fontSize: 10,
              p: 0,
            }}
          >
            {executionsOpen ? "Hide" : "Show"} {executions.length} sub-step
            {executions.length === 1 ? "" : "s"}
          </Typography>
          <Collapse in={executionsOpen}>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {executions.map((execution) => (
                <Stack
                  key={execution.id}
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  title={execution.logRef ?? undefined}
                  role={execution.logRef ? "button" : undefined}
                  tabIndex={execution.logRef ? 0 : undefined}
                  onClick={(event) => {
                    if (!execution.logRef || !onExecutionClick) return;
                    event.stopPropagation();
                    onExecutionClick(node.id, execution.id);
                  }}
                  onKeyDown={(event) => {
                    if (
                      execution.logRef &&
                      onExecutionClick &&
                      (event.key === "Enter" || event.key === " ")
                    ) {
                      event.preventDefault();
                      event.stopPropagation();
                      onExecutionClick(node.id, execution.id);
                    }
                  }}
                  sx={{
                    cursor: execution.logRef ? "pointer" : "default",
                    "&:hover": execution.logRef
                      ? { bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }
                      : undefined,
                  }}
                >
                  <StatusDot status={toExecutionStatus(execution.status)} />
                  <Typography
                    sx={{
                      fontSize: 9,
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {execution.name}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

function toExecutionStatus(status: string): DagStatus {
  if (status === "completed" || status === "success") return "success";
  if (status === "failed") return "failed";
  if (status === "in_progress" || status === "running") return "running";
  if (status === "skipped") return "skipped";
  return "pending";
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PipelineDAG({
  nodes,
  edges,
  phases,
  selectedNodeId,
  onNodeClick,
  onExecutionClick,
  loading = false,
}: PipelineDAGProps): React.ReactElement {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [edgePaths, setEdgePaths] = useState<EdgePath[]>([]);

  // Group nodes by phase for column rendering.
  const nodesByPhase = useMemo(() => {
    const map = new Map<DagPhase, DagNodeType[]>();
    for (const phase of phases) {
      map.set(
        phase,
        nodes.filter((n) => n.data.phase === phase),
      );
    }
    return map;
  }, [nodes, phases]);

  // Draw SVG bezier edges after layout, matching the mockup's drawEdges().
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || nodes.length === 0 || edges.length === 0) {
      setEdgePaths([]);
      return;
    }

    const canvasRect = canvas.getBoundingClientRect();

    // Build a map of node id → element rect.
    const rectMap = new Map<string, DOMRect>();
    const nodeEls = canvas.querySelectorAll<HTMLElement>("[data-node-id]");
    for (const el of nodeEls) {
      const id = el.dataset.nodeId;
      if (id) rectMap.set(id, el.getBoundingClientRect());
    }

    const paths: EdgePath[] = [];
    for (const edge of edges) {
      const sourceRect = rectMap.get(edge.source);
      const targetRect = rectMap.get(edge.target);
      if (!sourceRect || !targetRect) continue;

      const x1 = sourceRect.right - canvasRect.left;
      const y1 = sourceRect.top + sourceRect.height / 2 - canvasRect.top;
      const x2 = targetRect.left - canvasRect.left;
      const y2 = targetRect.top + targetRect.height / 2 - canvasRect.top;
      const midX = (x1 + x2) / 2;

      const d = `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
      paths.push({ d, status: edge.data.status });
    }

    setEdgePaths(paths);
  }, [nodes, edges, phases]);

  const handleClick = useCallback(
    (nodeId: string, data: DagNodeType["data"]) => {
      onNodeClick(nodeId, data);
    },
    [onNodeClick],
  );

  if (nodes.length === 0) {
    if (loading) {
      // Skeleton DAG: container with title placeholder + phase column skeletons
      return (
        <Box
          sx={{
            p: 2.5,
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: "12px",
            bgcolor: (t) => t.palette.background.paper,
          }}
        >
          <Skeleton variant="text" width={220} height={14} sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", gap: "56px" }}>
            {[1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 150,
                }}
              >
                <Skeleton
                  variant="text"
                  width={50}
                  height={12}
                  sx={{ mb: 1.25 }}
                />
                <Skeleton
                  variant="rounded"
                  width={150}
                  height={48}
                  sx={{ borderRadius: "10px", mb: 1.75 }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: "12px",
          bgcolor: (t) => t.palette.background.paper,
          p: 5,
          textAlign: "center",
        }}
      >
        <Typography color="text.secondary">
          No pipeline steps for this run.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2.5,
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: "12px",
        bgcolor: (t) => t.palette.background.paper,
        overflowX: "auto",
      }}
    >
      {/* DAG title */}
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "text.secondary",
          mb: 2,
        }}
      >
        Pipeline graph — click a job to view its logs
      </Typography>

      {/* Canvas with SVG overlay + flex columns */}
      <Box
        ref={canvasRef}
        sx={{
          position: "relative",
          minWidth: "max-content",
          py: 0.5,
        }}
      >
        {/* SVG edge overlay */}
        <svg
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {edgePaths.map((edge, i) => {
            const color = STATUS_COLORS[edge.status];
            return (
              <path
                key={i}
                d={edge.d}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
                strokeDasharray={
                  edge.status === "running"
                    ? "6 4"
                    : edge.status === "pending"
                      ? "3 3"
                      : edge.status === "skipped"
                        ? "2 4"
                        : undefined
                }
                style={{
                  animation:
                    edge.status === "running"
                      ? "dashflow 0.6s linear infinite"
                      : undefined,
                  opacity:
                    edge.status === "skipped"
                      ? 0.4
                      : edge.status === "pending"
                        ? 0.6
                        : 0.85,
                }}
              />
            );
          })}
        </svg>

        {/* Phase columns */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            gap: "56px",
          }}
        >
          {phases.map((phase) => (
            <Box
              key={phase}
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 150,
              }}
            >
              {/* Phase header — inside the container */}
              <Typography
                sx={{
                  fontSize: "10.5px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "text.secondary",
                  mb: 1.25,
                  pl: 0.5,
                }}
              >
                {PHASE_LABELS[phase]}
              </Typography>

              {/* Job nodes */}
              {(nodesByPhase.get(phase) ?? []).map((node) => (
                <JobNode
                  key={node.id}
                  node={node}
                  selected={node.id === selectedNodeId}
                  onClick={handleClick}
                  onExecutionClick={onExecutionClick}
                />
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Keyframe for running edge dash flow */}
      <style>{`
        @keyframes dashflow {
          to { stroke-dashoffset: -10; }
        }
      `}</style>
    </Box>
  );
}

export default PipelineDAG;
